import { BoxV2 as Box, Flex } from 'portal-commons';
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { faAngleLeft, faLink } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Container, Grid } from '@material-ui/core';

import {
  CTAMultiMediaList,
  CampaignBasicDetailsV2,
  CampaignBrandDetails,
  CampaignCspDetails,
  CampaignAttributes,
  CampaignDescription,
  CampaignSampleMessages,
  CarrierStatusSection,
  CampaignSampleMultimedia,
  NumbersProvisioned,
  DeclinedExplanation,
} from '../components';
import CampaignEventHistory from '../components/CampaignEventHistory';
import SharingChain from '../components/SharingChain';
import CampaignBrandAssetsSection from '../components/CampaignBrandAssetsSection';
import {
  brandController,
  campaignController,
  enumController,
} from '@/apis/core';
import { getRejectionCategoryOptions } from '@/apis/services';
import { BRAND_ASSET_ENABLED, TRACKING_REJECTED_CAMPAIGNS } from '@/constants';
import { useAuthContext } from '@/contexts';
import {
  CSP_CAMPAIGNS_DEFAULT_ENTRY,
  DECLINED_CSP_CAMPAIGNS_PATH,
  DECLINED_DCA_CAMPAIGNS_PATH,
} from '@/constants/paths';
import { Loader } from '@/shared_elements';
import { sortMnoList } from '@/utils';

import '@/assets/styles/campaign-detail-module.scss';

export default function CampaignDetail(props) {
  const { profile } = useAuthContext();
  const location = useLocation();
  const history = useHistory();
  const [loader, setLoader] = useState(true);
  const [campaignDetail, setCampaignDetail] = useState({});
  const [sharingNodes, setSharingNodes] = useState([]);
  const [brandMNOSuspensionDetail, setBrandMNOSuspensionDetail] = useState([]);
  const [migrationInfo, setMigrationInfo] = useState();
  const [provisional, setProvisional] = useState();
  const [isOnExistingChain, setIsOnExistingChain] = useState();
  const { id: campaignUid } = useParams();
  const [rejectionCategories, setRejectionCategories] = useState([]);
  const [verticalTypes, setVerticalTypes] = useState([]);
  const isDeclined = campaignDetail.sharingStatus === 'DECLINED';

  useEffect(() => {
    fetchRejectionCategory();
    fetchVerticalTypes();
  }, []);

  const fetchRejectionCategory = async () => {
    const categories = await getRejectionCategoryOptions();
    setRejectionCategories(categories);
  };

  const fetchVerticalTypes = async () => {
    const response = await enumController.getVerticalTypes();
    setVerticalTypes(response);
  };

  useEffect(() => {
    updateProvisionalFlag(campaignUid);
    return () => {
      setProvisional(undefined);
      setCampaignDetail({});
    };
  }, [campaignUid]);

  useEffect(() => {
    if (campaignUid && provisional !== null && provisional !== undefined) {
      updateProvisionalFlagDependentData(campaignUid, provisional);
    }
  }, [campaignUid, provisional]);

  const updateProvisionalFlag = async (campaignUid) => {
    let provisional = false;
    const migrationInfo = await campaignController.getMigrateCnp(
      campaignUid,
      null,
      { silent: true }
    );
    setMigrationInfo(migrationInfo);

    try {
      const roles = await campaignController.getCampaignRoles(campaignUid);
      const isOnChain = (role) => role && !['NONE', 'UNSET'].includes(role);

      setIsOnExistingChain(isOnChain(roles.normalRole));
      provisional =
        migrationInfo?.status === 'ACTIVE' && isOnChain(roles?.provisionalRole);
    } catch {
      setIsOnExistingChain(true);
    }
    setProvisional(provisional);
  };

  const updateProvisionalFlagDependentData = (campaignUid, provisional) => {
    fetchCampaignDetail(campaignUid, provisional);
    updateSharingChain(campaignUid, provisional);
  };

  const fetchCampaignDetail = async (campaignUid, provisional) => {
    const response = await campaignController.getCampaignDetail(campaignUid, {
      provisional,
    });
    if (response) {
      setCampaignDetail(response);
      setLoader(false);
    }
  };

  function splitArrayIntoChunks(arr, chunkSize) {
    if (!Array.isArray(arr) || !Number.isInteger(chunkSize) || chunkSize <= 0) {
      throw new Error(
        'Invalid input. The first argument should be an array and the second argument should be a positive integer.'
      );
    }

    const result = [];
    for (let i = 0; i < chunkSize; i++) {
      const chunk = [];
      for (let j = i; j < arr.length; j += chunkSize) {
        chunk.push(arr[j]);
      }
      result.push(chunk);
    }
    return result.reduce((flattened, chunk) => flattened.concat(chunk), []);
  }

  const fetchBrandMNOSuspensionDetail = async (brandUid) => {
    const response = await brandController.getMnoSuspensionStatus(brandUid);
    const mnoMetaData = await enumController.getMnos();
    let mappedResponse = response.map((suspendedMNO) => {
      const result = { ...suspendedMNO };
      mnoMetaData.forEach((mnoData) => {
        if (suspendedMNO.mnoId === mnoData.networkId) {
          result.displayName = mnoData.displayName;
          result.osrBitmaskIndex = mnoData.osrBitmaskIndex;
        }
      });
      return result;
    });
    // Sorting
    mappedResponse = splitArrayIntoChunks(
      sortMnoList(mappedResponse, ['displayName']),
      2
    );
    setBrandMNOSuspensionDetail(mappedResponse);
  };

  const updateSharingChain = async (campaignUid, provisional) => {
    const sharingChain = await campaignController.getSharingChain(campaignUid, {
      provisional,
    });
    setSharingNodes(sharingChain?.nodes ?? []);
  };

  useEffect(async () => {
    if (campaignDetail.brandUid) {
      await fetchBrandMNOSuspensionDetail(campaignDetail.brandUid);
    }
  }, [campaignDetail]);

  const handleGoBack = () => {
    if (location.state?.goBackPage) {
      props.history.push(location.state.goBackPage);
    } else {
      props.history.push(CSP_CAMPAIGNS_DEFAULT_ENTRY);
    }
  };

  const handleAcceptElection = async () => {
    setProvisional(undefined);
    await updateProvisionalFlag(campaignUid);
  };

  const backLinkText = useMemo(() => {
    const goBackPage = location.state?.goBackPage;
    if (!goBackPage) return 'back';

    if (
      goBackPage.startsWith(DECLINED_CSP_CAMPAIGNS_PATH) ||
      goBackPage.startsWith(DECLINED_DCA_CAMPAIGNS_PATH)
    ) {
      return 'Back to Declined Campaigns';
    }

    if (['/dashboard'].includes(goBackPage)) return 'back to dashboard';

    if (goBackPage.indexOf('/phones') > -1) {
      return 'back to phone number history';
    }

    return 'back to campaigns';
  }, [location.state]);

  return (
    <section className="campaign-detail-section" data-testid="campaignDetail">
      <Container maxWidth={false} className="campaign-detail-container">
        {loader || !campaignDetail ? (
          <Loader />
        ) : (
          <Grid container>
            <Grid item xs={12}>
              <div className="top-block">
                <Box className="back-btn">
                  <Flex
                    as="a"
                    sx={{ mb: 'xl', width: 'fit-content', cursor: 'pointer' }}
                    onClick={handleGoBack}
                    data-testid="campaignDetailBackButton"
                  >
                    <Box sx={{ display: 'inline-flex', flexDirection: 'row' }}>
                      <Box sx={{ mr: 'xs' }}>
                        <FontAwesomeIcon icon={faAngleLeft} color="#C4C8C9" />
                      </Box>
                      <span>{backLinkText}</span>
                    </Box>
                  </Flex>
                </Box>
                <CampaignBasicDetailsV2
                  campaign={campaignDetail}
                  migrationInfo={migrationInfo}
                />
                {TRACKING_REJECTED_CAMPAIGNS && isDeclined && (
                  <DeclinedExplanation
                    campaign={campaignDetail}
                    rejectionCategories={rejectionCategories}
                  />
                )}
              </div>
            </Grid>
            {!isDeclined && (
              <Grid item xs={12}>
                <CarrierStatusSection
                  profile={profile}
                  campaign={campaignDetail}
                  onAcceptElection={handleAcceptElection}
                  provisional={provisional}
                  existingChain={isOnExistingChain}
                  goBackPage={location.state?.goBackPage}
                  onMnoExclusionRemove={(mno) => {
                    setCampaignDetail({
                      ...campaignDetail,
                      excludedMnoList: campaignDetail.excludedMnoList.filter(
                        (excludedMno) => excludedMno.id !== mno.id
                      ),
                    });
                  }}
                />
              </Grid>
            )}
            {!isDeclined && (
              <Grid item xs={12}>
                <Box className="details-wrapper" data-testid="sharingChain">
                  <Grid
                    className="title"
                    container
                    direction="row"
                    alignItems="center"
                  >
                    <h3 className="heading2">
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}
                      >
                        <Box sx={{ mr: 'xs' }}>
                          <FontAwesomeIcon icon={faLink} />
                        </Box>
                        <span>Connectivity Chain</span>
                      </Box>
                    </h3>
                  </Grid>
                  <SharingChain
                    nodes={
                      campaignDetail.isPrimaryDca ||
                      campaignDetail.secondarySharingStatus === 'ACCEPTED'
                        ? sharingNodes
                        : sharingNodes.slice(0, -1)
                    }
                  />
                </Box>
              </Grid>
            )}
            {!isDeclined && (
              <Grid item xs={12}>
                <NumbersProvisioned campaignUid={campaignDetail.uid} />
              </Grid>
            )}
            <Grid item xs={12}>
              <CampaignBrandDetails
                profile={profile}
                verticalTypes={verticalTypes}
                campaign={campaignDetail}
                brandMNOSuspension={brandMNOSuspensionDetail}
              />
            </Grid>
            {BRAND_ASSET_ENABLED &&
              campaignDetail.brand?.entityType !== 'SOLE_PROPRIETOR' && (
                <Grid item xs={12}>
                  <CampaignBrandAssetsSection
                    brandId={campaignDetail.brandUid}
                  />
                </Grid>
              )}
            <Grid item xs={12}>
              <CampaignCspDetails campaign={campaignDetail} />
            </Grid>
            <Grid item xs={12}>
              <CampaignAttributes campaign={campaignDetail} />
            </Grid>
            <Grid item xs={12}>
              <CampaignDescription campaign={campaignDetail} />
            </Grid>
            <Grid item xs={12}>
              <CTAMultiMediaList campaignUid={campaignUid} />
            </Grid>
            <Grid item xs={12}>
              <CampaignSampleMessages campaign={campaignDetail} />
            </Grid>
            <Grid item xs={12}>
              <CampaignSampleMultimedia campaignUid={campaignUid} />
            </Grid>
            {!isDeclined && (
              <Grid item xs={12}>
                <CampaignEventHistory campaignUid={campaignUid} />
              </Grid>
            )}
          </Grid>
        )}
      </Container>
    </section>
  );
}
